import type { ReactNode } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * A decoration attached to one integer position of the slider.
 *
 * Note on index alignment: a NON-NEGATIVE slider index is also an index into the
 * availability `available_steps` array - see `handleUpdateAnimationStep` in Timeline.tsx,
 * which does `available_steps[val]` for `val >= 0`. So a cadence change found at
 * `available_steps` index 7 maps to slider index 7 with no offset, even when min is -12.
 * NEGATIVE indices have no backing `available_steps` entry at all - they mean
 * "go back |index| hours into a previous forecast run".
 */
export interface TimelineTick {
    /** Integer position. Ticks outside [min, max] are silently ignored. */
    index: number,
    /** Vertical rule on the LEFT edge of this cell, i.e. the boundary index-1 | index. */
    divider?: boolean,
    /** Icon above the track, centred on this cell. Absolutely positioned, so it never widens the cell. */
    icon?: IconDefinition,
    /** Tooltip for this cell. Takes precedence over the slider's renderTooltip(index). */
    tooltip?: ReactNode,
    /** Extra class on the cell, for one-off styling. */
    className?: string,
}

/** Describes one cadence transition found in a sparse step array. */
export interface CadenceChange {
    /** First index at which the new cadence applies. */
    index: number,
    step: number,
    previousStep: number,
    cadence: number,
    previousCadence: number,
}

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Finds every position where the spacing of a sparse ascending step array changes,
 * e.g. [0,1,2,3,4,5,6,12,18,24] -> one tick at index 7 (1-hourly becomes 6-hourly).
 *
 * Non-ascending or duplicate entries are skipped rather than throwing - the availability
 * payload is server-provided and occasionally malformed.
 */
export const deriveCadenceTicks = (
    availableSteps: number[],
    options?: {
        icon?: IconDefinition,
        divider?: boolean,
        describeChange?: (change: CadenceChange) => ReactNode,
    },
): TimelineTick[] => {
    const ticks: TimelineTick[] = [];
    let previousCadence: number | undefined;

    for (let index = 1; index < availableSteps.length; index++) {
        const cadence = availableSteps[index] - availableSteps[index - 1];
        if (!Number.isFinite(cadence) || cadence <= 0) continue;

        if (previousCadence === undefined) {
            previousCadence = cadence;
            continue;
        }
        if (cadence === previousCadence) continue;

        ticks.push({
            index,
            divider: options?.divider ?? true,
            icon: options?.icon,
            tooltip: options?.describeChange?.({
                index,
                step: availableSteps[index],
                previousStep: availableSteps[index - 1],
                cadence,
                previousCadence,
            }),
        });
        previousCadence = cadence;
    }

    return ticks;
};
